import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useOrg } from 'src/shared/contexts/OrgContext';
import mixpanel from 'src/shared/lib/mixpanel';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { Entity, OutputFormat } from 'src/shared/types/extraction-model.types';

type NewEntityProps = {
  name: string;
  description?: string;
  outputFormat: OutputFormat;
  parentEntityId?: string;
};

type UpdateEntityProps = {
  entityId: string;
  name?: string;
  description?: string;
  outputFormat?: OutputFormat;
  parentEntityId?: string | null;
};

export function useMutateExtractionEntity(modelId: string) {
  const queryClient = useQueryClient();
  const { orgFetch, currentOrg, role } = useOrg();
  const { errorToast } = useToast();

  const modelQueryKey = ['next-gen', 'models', modelId, genQueryKey(currentOrg.id)];
  const modelsListQueryKey = ['next-gen', 'models', 'extraction', genQueryKey(currentOrg.id)];

  const newEntity = useMutation({
    mutationFn: async ({ name, description, outputFormat, parentEntityId }: NewEntityProps) => {
      if (['READER', 'SALES'].includes(role)) {
        throw new Error('Users with Reader or Sales role cannot create entities');
      }
      const res = await orgFetch<Entity>(`/api/v2/next-gen-models/extraction/${modelId}/entities`, {
        method: 'POST',
        body: JSON.stringify({
          name,
          description,
          outputFormat,
          parentEntityId,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error('Unable to create entity');
      }
      return data;
    },
    onSuccess: (_data: Entity) => {
      // Invalidate queries to refetch the updated model
      queryClient.invalidateQueries({ queryKey: modelQueryKey });
      mixpanel.track('Next-Gen Extraction Entity Created');
    },
    onError: () => {
      errorToast('Failed to create entity');
    },
  });

  const updateEntity = useMutation({
    mutationFn: async ({ entityId, ...data }: UpdateEntityProps) => {
      if (['READER', 'SALES'].includes(role)) {
        throw new Error('Users with Reader or Sales role cannot update entities');
      }
      const res = await orgFetch<Entity>(`/api/v2/next-gen-models/extraction/${modelId}/entities/${entityId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error('Entity update failed');
      }
      return responseData;
    },
    onSuccess: (_data, variables) => {
      // Invalidate queries to refetch the updated model
      queryClient.invalidateQueries({ queryKey: modelQueryKey });
      queryClient.invalidateQueries({ queryKey: modelsListQueryKey });

      // If this is a custom format (formatId === 'custom'), invalidate formats query
      if (variables.outputFormat?.formatId === 'custom') {
        const formatsQueryKey = ['next-gen', 'models', modelId, 'formats', genQueryKey(currentOrg.id)];
        queryClient.invalidateQueries({ queryKey: formatsQueryKey });
      }

      mixpanel.track('Next-Gen Extraction Entity Updated');
    },
    onError: () => {
      errorToast('Failed to update entity');
    },
  });

  const deleteEntity = useMutation({
    mutationFn: async (entityId: string) => {
      if (['READER', 'SALES'].includes(role)) {
        throw new Error('Users with Reader or Sales role cannot delete entities');
      }
      const res = await orgFetch<{ message: string }>(
        `/api/v2/next-gen-models/extraction/${modelId}/entities/${entityId}`,
        {
          method: 'DELETE',
        }
      );
      if (!res.ok) {
        throw new Error('Unable to delete entity');
      }
      return entityId;
    },
    onSuccess: () => {
      // Invalidate queries to refetch the updated model
      queryClient.invalidateQueries({ queryKey: modelQueryKey });
      queryClient.invalidateQueries({ queryKey: modelsListQueryKey });
      mixpanel.track('Next-Gen Extraction Entity Deleted');
    },
    onError: () => {
      errorToast('Failed to delete entity');
    },
  });

  return {
    newEntity,
    updateEntity,
    deleteEntity,
  };
}
